// PurchaseOrderFormModal — create/edit/delete of a purchase order and its
// budget lines. Presentational + local state: the mutation callbacks
// (onCreate/onUpdate/onDelete) are injected by FinanceTab, which owns the
// React Query hooks (useCreate/useUpdate/useDeletePurchaseOrder) and the
// cache invalidation. On success the modal closes and the invalidated GET
// refetches the derived balances — this modal never reads a mutation response.
//
// Header fields (PO number, description, total value ex-GST, status, issued
// date) plus an inline line editor (PoLineEditor) per budget line. GST is
// decided per line (PoLineEditor's own checkbox), not at the PO header — the
// total value is always ex-GST. Line ids are preserved on edit so recorded
// invoice splits survive renumbering. A 409 on save is the optimistic-lock
// conflict (surface a reload prompt); a 409 on delete means invoices
// reference the PO (surface the backend message). The rate-item/milestone/SOW
// tag option lists are sourced by FinanceTab (from finance setup/summary and
// tracking data) and threaded through to each PoLineEditor unchanged.
//
// Lines collapse/expand independently (state owned here, PoLineEditor is
// presentational): create mode opens with its single starter line expanded
// so a first-time PO reads like today's form; edit mode opens every line
// collapsed so a PO with many lines reads as a compact register. Add line
// collapses whatever was open, appends and expands the new line at the
// bottom (below the "Add line" button's own row), and focuses+scrolls to its
// line-number field. A failed submit never leaves a validation error hidden
// inside a collapsed line: every errored line is force-expanded and focus
// moves to the first one's actual failing field (not merely its first
// field), so the error is always reachable without manually reopening rows.

import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Alert from "../../common/Alert";
import CurrencyInput from "../../common/CurrencyInput";
import PoLineEditor, { poLineFieldId } from "./PoLineEditor";
import type { TagOption } from "./TagMultiSelect";
import { isConflictError } from "../../../services/api";
import { formatAud } from "../../../utils/financeCalc";
import {
  buildEmptyPoFormState,
  hasErrors,
  makeEmptyLine,
  nextLineNumber,
  parseMoney,
  stateFromPurchaseOrder,
  sumLineTotals,
  tagsUsedOnOtherLines,
  toCreateInput,
  toUpdateInput,
  updateLine,
  validatePoForm,
  type PoFieldErrors,
  type PoFormState,
  type PoLineErrorField,
} from "./poFormState";
import type {
  CreatePurchaseOrderInput,
  PurchaseOrderStatus,
  PurchaseOrderWithDerived,
  UpdatePurchaseOrderInput,
} from "../../../types/finance";

export type PoFormMode = "create" | "edit";

interface PurchaseOrderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: PoFormMode;
  source?: PurchaseOrderWithDerived;
  /** Contract-level GST intent, used to default new lines' GST-applies flag on create. */
  contractGstApplies?: boolean;
  onCreate?: (input: CreatePurchaseOrderInput) => Promise<unknown>;
  onUpdate?: (input: UpdatePurchaseOrderInput) => Promise<unknown>;
  onDelete?: () => Promise<unknown>;
  isSubmitting?: boolean;
  /** Tag option lists forwarded to every PoLineEditor's TagMultiSelects. */
  rateItemOptions: TagOption[];
  milestoneOptions: TagOption[];
  sowOptions: TagOption[];
}

const STATUS_OPTIONS: { value: PurchaseOrderStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

const MODE_TITLE: Record<PoFormMode, string> = {
  create: "Add purchase order",
  edit: "Edit purchase order",
};

export default function PurchaseOrderFormModal({
  isOpen,
  onClose,
  mode,
  source,
  contractGstApplies,
  onCreate,
  onUpdate,
  onDelete,
  isSubmitting,
  rateItemOptions,
  milestoneOptions,
  sowOptions,
}: PurchaseOrderFormModalProps) {
  const [state, setState] = useState<PoFormState>(buildEmptyPoFormState);
  const [errors, setErrors] = useState<PoFieldErrors>({
    lineErrors: {},
    lineErrorFields: {},
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Which lines render expanded — UI state only, never serialised. Reseeded
  // with the form state on every open: create starts with its single starter
  // line open, edit starts fully collapsed so a large PO reads as a compact
  // register. `pendingFocus` carries "focus this line's named field after the
  // next render" for the add-line reveal (first field) and the failed-submit
  // reveal (the actual failing field).
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const pendingFocus = useRef<{
    key: string;
    field: PoLineErrorField;
  } | null>(null);

  // Render-time seed guard (Pattern 0): re-seed each time the modal opens or
  // its target changes, and reset on close so a reopen never retains prior
  // input. The modal is eager-mounted (only isOpen toggles).
  const seedKey = isOpen ? `${mode}:${source?.id ?? "new"}` : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    if (mode === "edit" && source) {
      setState(stateFromPurchaseOrder(source));
      setExpandedKeys(new Set());
    } else {
      const empty = buildEmptyPoFormState();
      if (contractGstApplies !== undefined) {
        empty.lines = empty.lines.map((line) => ({
          ...line,
          gstApplies: contractGstApplies,
        }));
      }
      setState(empty);
      setExpandedKeys(new Set(empty.lines.map((l) => l.key)));
    }
    setErrors({ lineErrors: {}, lineErrorFields: {} });
    setSubmitError(null);
    setConfirmingDelete(false);
  } else if (seedKey === null && seededFor !== null) {
    setSeededFor(null);
  }

  const patchHeader = (patch: Partial<PoFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  };

  const patchLine = (
    index: number,
    patch: Partial<PoFormState["lines"][number]>,
  ) => {
    setState((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? updateLine(line, patch) : line,
      ),
    }));
  };

  const addLine = () => {
    const newLine = makeEmptyLine(nextLineNumber(state.lines));
    setState((prev) => ({ ...prev, lines: [...prev.lines, newLine] }));
    setExpandedKeys(new Set([newLine.key]));
    pendingFocus.current = { key: newLine.key, field: "line-number" };
  };

  const toggleLine = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeLine = (index: number) => {
    setState((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index),
    }));
  };

  // Live reconciliation readout: budget lines vs the PO total, both ex-GST.
  // Only meaningful once a positive PO total has been entered — otherwise
  // there is nothing yet to compare against.
  const linesSum = sumLineTotals(state.lines);
  const poTotal = parseMoney(state.totalValue);
  const reconciled =
    poTotal !== null && poTotal > 0
      ? Math.round(linesSum * 100) === Math.round(poTotal * 100)
      : null;
  const reconDiff = poTotal !== null ? linesSum - poTotal : 0;

  // Focus and reveal a just-added or just-revealed-invalid line's target
  // field. Runs every render; the ref is null except immediately after
  // addLine/failed submit.
  useEffect(() => {
    if (pendingFocus.current === null) return;
    const { key, field } = pendingFocus.current;
    pendingFocus.current = null;
    const el = document.getElementById(poLineFieldId(key, field));
    if (el instanceof HTMLElement) {
      el.focus();
      el.scrollIntoView?.({ block: "center" });
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validatePoForm(state);
    if (hasErrors(v)) {
      setErrors(v);
      // Never leave a validation error hidden inside a collapsed line: reveal
      // every errored line and take the user to the first one's failing field.
      const errored = state.lines.filter((l) => v.lineErrors[l.key]);
      if (errored.length > 0) {
        setExpandedKeys((prev) => {
          const next = new Set(prev);
          for (const l of errored) next.add(l.key);
          return next;
        });
        pendingFocus.current = {
          key: errored[0].key,
          field: v.lineErrorFields[errored[0].key] ?? "line-number",
        };
      }
      return;
    }
    setErrors({ lineErrors: {}, lineErrorFields: {} });
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        await onUpdate(toUpdateInput(state, source.version));
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateInput(state));
      }
      onClose();
    } catch (err) {
      if (isConflictError(err)) {
        setSubmitError(
          "This purchase order was changed elsewhere — reload and try again.",
        );
      } else {
        setSubmitError(
          err instanceof Error
            ? err.message
            : "Failed to save the purchase order.",
        );
      }
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSubmitError(null);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      // A 409 here means invoices reference the PO — the backend message names
      // the cause, so surface it verbatim rather than a generic reload prompt.
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Failed to delete the purchase order.",
      );
      setConfirmingDelete(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={MODE_TITLE[mode]}
      maxWidth="sm:max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="po-number" className="label">
              PO number <span className="text-red-600">*</span>
            </label>
            <input
              id="po-number"
              type="text"
              className="input"
              value={state.poNumber}
              onChange={(e) => patchHeader({ poNumber: e.target.value })}
              disabled={isSubmitting}
              aria-invalid={!!errors.poNumber}
            />
            {errors.poNumber && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.poNumber}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="po-description" className="label">
              Description
            </label>
            <input
              id="po-description"
              type="text"
              className="input"
              value={state.description}
              onChange={(e) => patchHeader({ description: e.target.value })}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label htmlFor="po-total" className="label">
              Total value (ex-GST) <span className="text-red-600">*</span>
            </label>
            <CurrencyInput
              id="po-total"
              size="md"
              value={state.totalValue}
              onChange={(raw) => patchHeader({ totalValue: raw })}
              ariaInvalid={!!errors.totalValue}
              disabled={isSubmitting}
            />
            {errors.totalValue && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.totalValue}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="po-issued" className="label">
              Issued date
            </label>
            <input
              id="po-issued"
              type="date"
              className="input"
              value={state.issuedDate}
              onChange={(e) => patchHeader({ issuedDate: e.target.value })}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label htmlFor="po-status" className="label">
              Status
            </label>
            <select
              id="po-status"
              className="input"
              value={state.status}
              onChange={(e) =>
                patchHeader({ status: e.target.value as PurchaseOrderStatus })
              }
              disabled={isSubmitting}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-h4 text-slate-900 dark:text-slate-100">
              Budget lines
            </h4>
          </div>
          {poTotal !== null && poTotal > 0 && (
            <p
              className={clsx(
                "text-caption tabular-nums",
                reconciled
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-amber-700 dark:text-amber-400",
              )}
            >
              Budget lines {formatAud(linesSum)} / PO total {formatAud(poTotal)}
              {reconciled
                ? " · ✓ reconciled"
                : ` · ${reconDiff > 0 ? "over" : "under"} by ${formatAud(Math.abs(reconDiff))}`}
            </p>
          )}
          {errors.lines && <Alert variant="error">{errors.lines}</Alert>}
          {errors.reconciliation && (
            <Alert variant="error">{errors.reconciliation}</Alert>
          )}
          {state.lines.map((line, index) => (
            <PoLineEditor
              key={line.key}
              line={line}
              error={errors.lineErrors[line.key]}
              errorField={errors.lineErrorFields[line.key]}
              onChange={(patch) => patchLine(index, patch)}
              onRemove={() => removeLine(index)}
              canRemove={state.lines.length > 1}
              disabled={isSubmitting}
              rateItemOptions={rateItemOptions}
              milestoneOptions={milestoneOptions}
              sowOptions={sowOptions}
              usedElsewhere={tagsUsedOnOtherLines(state.lines, index)}
              collapsed={!expandedKeys.has(line.key)}
              onToggleCollapsed={() => toggleLine(line.key)}
            />
          ))}
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addLine}
              disabled={isSubmitting}
            >
              Add line
            </Button>
          </div>
        </div>

        {submitError && <Alert variant="error">{submitError}</Alert>}

        <div className="flex items-center justify-between gap-3 pt-2">
          <div>
            {mode === "edit" && onDelete && !confirmingDelete && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmingDelete(true)}
                disabled={isSubmitting}
                className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
              >
                Delete purchase order
              </Button>
            )}
            {mode === "edit" && onDelete && confirmingDelete && (
              <div className="flex items-center gap-2">
                <span className="text-caption text-slate-600 dark:text-slate-400">
                  Delete this purchase order?
                </span>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Confirm delete
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={isSubmitting}
                >
                  Keep
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {mode === "edit" ? "Save changes" : "Create"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
